import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import ReactSelect from 'react-select'
import { SELECT_STYLES, SELECT_THEME, MENU_PORTAL_TARGET } from '../../ui/Select/Select'
import DropdownIndicator from '../../ui/Select/DropdownIndicator'
import ScopedSelectOption from './ScopedSelectOption'

const ALLOW_ALL = () => true
const HIDE_SEPARATOR = () => ({ display: 'none' })
const NO_OPTIONS = () => 'Aucune option disponible'
const EMPTY_SET = new Set()

const SELECT_COMPONENTS = { DropdownIndicator, Option: ScopedSelectOption }

const isGrouped = options => options.length > 0 && Boolean(options[0].options)

const matchesTerm = (label, term) => String(label).toLowerCase().includes(term)

const groupHeaderOption = group => ({
  value: `__group__${group.key}`,
  label: group.label,
  count: group.options.length,
  groupKey: String(group.key),
  isGroupHeader: true,
  isDisabled: true
})

const buildFlatOptions = (groups, collapsed, term) => {
  if (term)
    return groups.flatMap(group => group.options.filter(option => matchesTerm(option.label, term)))
  return groups.flatMap(group => {
    const header = groupHeaderOption(group)
    return collapsed.has(String(group.key)) ? [header] : [header, ...group.options]
  })
}

const optionShape = PropTypes.shape({
  label: PropTypes.node,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
})

const ScopedSelect = ({ options, value, onChange, placeholder, isDisabled, menuPortal }) => {
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set())
  const [term, setTerm] = useState('')
  const grouped = isGrouped(options)
  const locked = grouped && options.length === 1

  const flatOptions = useMemo(() => {
    if (!grouped)
      return options
    return buildFlatOptions(options, locked ? EMPTY_SET : collapsedGroups, term.trim().toLowerCase())
  }, [grouped, locked, options, collapsedGroups, term])

  const styles = useMemo(() => ({
    ...SELECT_STYLES,
    indicatorSeparator: HIDE_SEPARATOR,
    option: (base, state) => {
      const inherited = SELECT_STYLES.option(base)
      if (state.data.isGroupHeader || !grouped)
        return inherited
      return { ...inherited, paddingLeft: 32 }
    }
  }), [grouped])

  const onToggleGroup = useCallback(groupKey => setCollapsedGroups(prev => {
    const next = new Set(prev)
    if (next.has(groupKey))
      next.delete(groupKey)
    else
      next.add(groupKey)
    return next
  }), [])

  const handleChange = useCallback(option => {
    if (option && option.isGroupHeader)
      return
    onChange(option)
  }, [onChange])

  const handleInputChange = useCallback((inputValue, meta) => {
    if (meta.action === 'input-change')
      setTerm(inputValue)
  }, [])

  const handleMenuClose = useCallback(() => setTerm(''), [])

  const groupedProps = {}
  if (grouped) {
    groupedProps.filterOption = ALLOW_ALL
    groupedProps.inputValue = term
    groupedProps.onInputChange = handleInputChange
    groupedProps.onMenuClose = handleMenuClose
  }

  return (
    <ReactSelect
      {...groupedProps}
      collapsedGroups={collapsedGroups}
      components={SELECT_COMPONENTS}
      locked={locked}
      isDisabled={isDisabled}
      menuPortalTarget={menuPortal ? MENU_PORTAL_TARGET : null}
      noOptionsMessage={NO_OPTIONS}
      options={flatOptions}
      placeholder={placeholder}
      styles={styles}
      theme={SELECT_THEME}
      value={value}
      onChange={handleChange}
      onToggleGroup={onToggleGroup}
    />
  )
}

ScopedSelect.propTypes = {
  isDisabled: PropTypes.bool,
  menuPortal: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.node,
    options: PropTypes.arrayOf(optionShape),
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  })).isRequired,
  placeholder: PropTypes.string,
  value: optionShape
}

ScopedSelect.defaultProps = {
  isDisabled: false,
  menuPortal: false,
  placeholder: 'Sélectionner...',
  value: null
}

export default ScopedSelect
